// Metres credited to one drum that are physically on another.
//
// The queue's own repair, and the one that had no verb. 2026-10-22 on L70 —
// CYYF 3x2.5, three colours, 10 000 m each, the line stopped:
//
//   BLUE   DONE     15 021 / 10 000      ← closed by hand at what the counter read
//   BROWN  PENDING       0 / 10 000      ← on the machine, 5 020.5 m already counted
//   GREEN-YELLOW PENDING 0 / 10 000
//
// The drum change to brown was never registered, so blue's record swallowed the
// brown bobbin's first 5 021 m. What the planner needs to say is one sentence —
// "5 021 of blue's are brown's" — and none of the four repairs could express it:
//
//   · "move to another product" re-points a WHOLE drum, and refuses a target in
//     the same product. Brown — same job, next task — could never be in its
//     list. The list was every job in the factory.
//   · split cuts the record in two and then asks which PRODUCT the tail belongs
//     to. Same dead end, one step later.
//
// So the target is a DRUM, named token by token out of the machine's own queue.
// The write is two length corrections (`/jobs/reconcile-lengths`) — the same
// endpoint the reconcile wizard posts, so each side re-derives its task, its
// material and its inputs' consumption.
//
// Pure, and mirrored to tests/.generated: the arithmetic is the whole of it, and
// getting the two items' sum wrong invents or destroys cable.

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const round3 = v => Math.round(num(v) * 1000) / 1000;
const up = v => String(v ?? '').trim().toUpperCase();

/**
 * Metres, as typed on a Romanian keyboard.
 *
 * An empty field is NaN, not 0. `Number("")` and `Number(null)` are both 0, and
 * on a length that reads as "hand over nothing" — which is indistinguishable
 * from a field nobody has filled in yet. The same slip closed a drum at 0 m in
 * the worker and grouped drums under `wd::0`; here it would arm the button with
 * no amount in it.
 */
export const asMetres = v => {
  const s = String(v ?? '').trim();
  if (!s) return NaN;
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Every drum on this machine's queue, one entry per DRUM.
 *
 * The queue DTO is already per token — each queue entry names one — so this is a
 * flatten, not a choice: the complaint it answers is that the only way to pick a
 * target was a product, and a product is not a bobbin.
 */
export function lineDrumsOf(queueRows = []) {
  const out = [];
  for (const g of queueRows || []) {
    for (const t of g?.queue || []) {
      const o = (t?.outputs || [])[0] || {};
      if (!o.tokenId) continue;
      out.push({
        key: `${g.jobId}::${t.taskId}::${o.tokenId}`,
        jobId: String(g.jobId),
        taskId: t.taskId ?? null,
        tokenId: String(o.tokenId),
        product: g.productLabel || g.name || g.jobId,
        phase: t.phase ?? null,
        color: t.color ?? o.color ?? null,
        status: up(o.status || t.status || 'PENDING'),
        planned: num(o.plannedLength ?? t.plannedLength),
        produced: num(o.producedLength),
        // The drum the worker is crediting right now. NOT derivable from the
        // status — an adopted drum stays PENDING until it closes — so the queue
        // endpoint reports it.
        onTheLine: !!t.onTheLine,
        // `_queueSeq` — the index the entry had in the Redis list when it was
        // read, which is the order the machine will work in. The underscore is
        // the DTO's, not a typo: `queueSeq` reads as undefined and sorts every
        // drum into one bucket.
        seq: Number.isFinite(Number(t._queueSeq)) ? Number(t._queueSeq) : Number.MAX_SAFE_INTEGER
      });
    }
  }
  return out;
}

/**
 * The drums those metres could belong to.
 *
 * Same phase — a length of isolation is not a length of rope — and never the
 * drum they are coming off. ON THE LINE first, then queue order, because "the
 * next brown one" is a position in the queue and nothing else.
 */
export function handoverTargets(lineDrums = [], source = null, phase = null) {
  if (!source?.tokenId) return [];
  const want = String(phase || source.phase || '');
  return (lineDrums || [])
    .filter(d => d.tokenId !== String(source.tokenId))
    .filter(d => !want || String(d.phase || '') === want)
    .sort((a, b) => Number(b.onTheLine) - Number(a.onTheLine) || a.seq - b.seq);
}

/**
 * The metres a drum holds beyond its plan.
 *
 * The answer nine times out of ten, because a drum credited past its plan is
 * usually a drum that ate the next one — so it is offered as a button rather
 * than left to be worked out on screen.
 */
export function overrunOf(source) {
  const has = num(source?.producedLength);
  return Math.max(0, round3(has - num(source?.plannedLength)));
}

/** Can this be asked for at all? */
export function handoverIssue({ source, target, amount }) {
  const has = num(source?.producedLength);
  const n = Number(amount);

  if (!source?.tokenId) return 'choose the drum the metres are credited to';
  if (has <= 0) return `${source.tokenId} has no metres on it — cancel it instead`;
  if (!Number.isFinite(n) || n <= 0) return 'how many metres are not its own?';
  if (round3(n) > round3(has)) return `${source.tokenId} only holds ${round3(has)} m`;
  if (!target?.tokenId) return 'choose the drum they are really on';
  if (String(target.tokenId) === String(source.tokenId)) return 'that is the same drum';
  return null;
}

/**
 * The two length corrections, as `/jobs/reconcile-lengths` takes them.
 *
 * Both sides in ONE request: the endpoint plans every item against the loaded
 * documents before it writes any of them, so a rejected side cannot leave half a
 * handover applied — which would be metres that exist on neither drum.
 */
export function handoverItems({ sourceJobId, source, target, amount }) {
  const n = round3(amount);
  return [
    {
      jobId: String(sourceJobId),
      tokenId: String(source.tokenId),
      producedLength: round3(num(source.producedLength) - n)
    },
    {
      jobId: String(target.jobId),
      tokenId: String(target.tokenId),
      producedLength: round3(num(target.produced) + n)
    }
  ];
}

const drumHandover = {
  lineDrumsOf,
  handoverTargets,
  overrunOf,
  handoverIssue,
  handoverItems,
  asMetres
};

export default drumHandover;
